import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Chart, ChartConfiguration, ChartData, ChartEvent } from 'chart.js';
import { registerables } from 'chart.js';
import { forkJoin } from 'rxjs';
import { NewContenedorComponent } from './subpages/contenedores-adm/components/new-contenedor/new-contenedor.component';
import { NewMovimientoComponent } from './subpages/shared/components/movimientos/new-movimiento/new-movimiento.component';
import { NewMantenimientoComponent } from './subpages/shared/components/mantenimientos/new-mantenimiento/new-mantenimiento.component';
import { AuthService } from './../../../services/API/auth/auth.service';
import { ContenedoresService } from './../../../services/API/contenedores/contenedores.service';
import { NotificationServiceService } from './../../../services/notification-service/notification-service.service';

Chart.register(...registerables);

interface EstadoConfig {
  key: string;
  label: string;
  color: string;
  icon: string;
}

interface LegendItem {
  key: string;
  label: string;
  count: number;
  percentLabel: string;
  color: string;
}

@Component({
    selector: 'app-contenedores',
    templateUrl: './contenedores.component.html',
    styleUrls: ['./contenedores.component.css'],
    standalone: false
})
export class ContenedoresComponent implements OnInit, OnDestroy {

  readonly estados: EstadoConfig[] = [
    { key: 'stock', label: 'En stock', color: '#2E7D32', icon: 'home' },
    { key: 'cliente', label: 'En cliente', color: '#EF6C00', icon: 'person_pin_circle' },
    { key: 'maintenance', label: 'Mantenimiento', color: '#607D8B', icon: 'build' },
    { key: 'incidence', label: 'Incidencia', color: '#D32F2F', icon: 'report_problem' },
    { key: 'baja', label: 'Baja', color: '#C62828', icon: 'fiber_manual_record' },
  ];

  loading = true;

  total = 0;
  activos = 0;
  disponibilidad = 0;
  counts: Record<string, number> = { stock: 0, cliente: 0, maintenance: 0, incidence: 0, baja: 0 };
  legendItems: LegendItem[] = [];

  mantenimientosActivos: any[] = [];
  ultimosMovimientos: any[] = [];

  doughnutChartData: ChartData<'doughnut'> = {
    labels: [],
    datasets: [{
      data: [],
      backgroundColor: [],
      borderWidth: 0,
      hoverOffset: 6,
    }],
  };

  doughnutChartOptions: ChartConfiguration<'doughnut'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '72%',
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(0, 40, 70, 0.92)',
        padding: 12,
        callbacks: {
          label: (ctx) => {
            const value = ctx.parsed;
            const dataset = ctx.dataset.data as number[];
            const sum = dataset.reduce((acc, n) => acc + n, 0);
            return ` ${ctx.label}: ${value} (${this.formatPercent(value, sum)})`;
          },
        },
      },
    },
  };

  constructor(
    private dialog: MatDialog,
    private router: Router,
    public authService: AuthService,
    public contenedoresService: ContenedoresService,
    public notificationServiceService: NotificationServiceService,
  ) {}

  ngOnInit(): void {
    if (!this.authService.canAccessContenedores()) {
      this.router.navigate(['/dashboard/home']);
      return;
    }
    if (this.hasFullContenedoresAccess()) {
      this.loadDashboard();
    } else {
      this.loading = false;
    }
  }

  ngOnDestroy(): void {}

  loadDashboard(): void {
    this.loading = true;
    forkJoin({
      contenedores: this.contenedoresService.getContenedores('all'),
      mantenimientos: this.contenedoresService.getMantenimientosContenedores('all', 'all', 'all'),
      movimientos: this.contenedoresService.getMovimientosContenedores('all', 'all', 'all'),
    }).subscribe({
      next: ({ contenedores, mantenimientos, movimientos }) => {
        this.processContenedores(contenedores as any[]);
        this.processMantenimientos(mantenimientos as any[]);
        this.processMovimientos(movimientos as any[]);
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.notificationServiceService.openSnackBar('No se pudieron cargar los datos del dashboard.');
      },
    });
  }

  private processContenedores(contenedores: any[]): void {
    this.counts = { stock: 0, cliente: 0, maintenance: 0, incidence: 0, baja: 0 };

    for (const c of contenedores) {
      const estado = c.estado || 'stock';
      if (this.counts[estado] !== undefined) {
        this.counts[estado]++;
      }
    }

    this.total = contenedores.length;
    this.activos = this.total - this.counts['baja'];
    this.disponibilidad = this.activos
      ? Math.round((this.counts['stock'] / this.activos) * 100)
      : 0;

    this.legendItems = this.estados.map((e) => ({
      key: e.key,
      label: e.label,
      count: this.counts[e.key],
      percentLabel: this.formatPercent(this.counts[e.key], this.total),
      color: e.color,
    }));

    this.doughnutChartData = {
      labels: this.estados.map((e) => e.label),
      datasets: [{
        data: this.estados.map((e) => this.counts[e.key]),
        backgroundColor: this.estados.map((e) => e.color),
        borderWidth: 0,
        hoverOffset: 6,
      }],
    };
  }

  private processMantenimientos(mantenimientos: any[]): void {
    this.mantenimientosActivos = mantenimientos
      .filter((m) => m.estado !== 'finish')
      .slice(0, 8);
  }

  private processMovimientos(movimientos: any[]): void {
    this.ultimosMovimientos = movimientos.slice(0, 8);
  }

  onChartClick(event: { event?: ChartEvent; active?: object[] }): void {
    if (!event.active?.length) {
      return;
    }
    const index = (event.active[0] as { index: number }).index;
    const estado = this.estados[index]?.key;
    if (estado) {
      this.navigateToEstado(estado);
    }
  }

  navigateToEstado(estado: string): void {
    this.router.navigate(['/dashboard/contenedores-adm'], {
      queryParams: estado === 'all' ? {} : { estado },
    });
  }

  viewContenedor(id: number): void {
    this.router.navigate(['/dashboard/contenedor', id]);
  }

  getMovimientoLabel(mov: any): string {
    if (mov.tipo === 'entrega') {
      return mov.contenedorEntregaMatricula || '—';
    }
    if (mov.tipo === 'retirada') {
      return mov.contenedorRetiradaMatricula || '—';
    }
    const entrega = mov.contenedorEntregaMatricula || '—';
    const retirada = mov.contenedorRetiradaMatricula || '—';
    return `${entrega} ↔ ${retirada}`;
  }

  getMovimientoTipoLabel(tipo: string): string {
    const labels: Record<string, string> = {
      entrega: 'Entrega',
      retirada: 'Retirada',
      cambio: 'Cambio',
    };
    return labels[tipo] || tipo;
  }

  getMantenimientoEstadoLabel(estado: string): string {
    const labels: Record<string, string> = {
      start: 'Pendiente',
      process: 'En proceso',
      finish: 'Finalizado',
    };
    return labels[estado] || estado;
  }

  newMovimiento(): void {
    this.dialog.open(NewMovimientoComponent, {
      data: { contenedor: 'all' },
      width: '90%',
      maxWidth: '500px',
    });
  }

  newContenedor(): void {
    const newNivelDialog = this.dialog.open(NewContenedorComponent, {
      width: '90%',
      maxWidth: '500px',
    });
    newNivelDialog.afterClosed().subscribe((result) => {
      if (result) {
        this.contenedoresService.newContenedor(
          result['matricula'], result['matricula_old'], result['medidas'],
          result['tipo'], result['estado'], result['observaciones'],
        ).subscribe({
          next: () => {
            this.notificationServiceService.openSnackBar('Contenedor creado correctamente!');
            this.loadDashboard();
          },
          error: () => {
            this.notificationServiceService.openSnackBar('¡Se ha producido un error!');
          },
        });
      }
    });
  }

  newMantenimiento(): void {
    this.dialog.open(NewMantenimientoComponent, {
      data: { contenedor: 'all' },
      width: '90%',
      maxWidth: '500px',
    });
  }

  userRolCheck(permission: string): boolean {
    return this.authService.userRolCheck(permission);
  }

  hasFullContenedoresAccess(): boolean {
    return this.authService.hasFullContenedoresAccess();
  }

  canAccessContenedoresLimitado(): boolean {
    return !this.hasFullContenedoresAccess()
      && (this.authService.canAltaMovimiento() || this.authService.canAltaMantenimiento());
  }

  canAltaMovimiento(): boolean {
    return this.authService.canAltaMovimiento();
  }

  canAltaMantenimiento(): boolean {
    return this.authService.canAltaMantenimiento();
  }

  private formatPercent(count: number, total: number): string {
    if (!total || count === 0) {
      return '0%';
    }
    const raw = (count / total) * 100;
    if (raw > 0 && raw < 1) {
      return '<1%';
    }
    return `${Math.round(raw)}%`;
  }

}
